/**
 * Geometry utility functions for map calculations
 * 
 * IMPROVEMENT: Uses proper geometric centroid calculation instead of simple arithmetic mean
 * This ensures markers are positioned at the true center of mass of polygons.
 */

export interface LatLng {
  latitude: number;
  longitude: number;
}

/**
 * Calculate the true centroid (center of mass) of a polygon
 * This uses the shoelace formula to calculate the area-weighted centroid
 * @param coordinates Array of [longitude, latitude] coordinates
 * @returns The centroid as {latitude, longitude}
 */
export function calculatePolygonCentroid(coordinates: [number, number][]): LatLng {
  if (coordinates.length < 3) {
    // For degenerate cases, fall back to arithmetic mean
    const centerLat = coordinates.reduce((sum, [, lat]) => sum + lat, 0) / coordinates.length;
    const centerLng = coordinates.reduce((sum, [lng]) => sum + lng, 0) / coordinates.length;
    return { latitude: centerLat, longitude: centerLng };
  }

  let area = 0;
  let centroidLat = 0;
  let centroidLng = 0;

  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;
    const coordI = coordinates[i];
    const coordJ = coordinates[j];
    if (!coordI || !coordJ) continue;
    const [lngI, latI] = coordI;
    const [lngJ, latJ] = coordJ;

    // Shoelace formula for area
    const crossProduct = lngI * latJ - lngJ * latI;
    area += crossProduct;

    // Centroid calculation
    centroidLat += (latI + latJ) * crossProduct;
    centroidLng += (lngI + lngJ) * crossProduct;
  }

  area /= 2;

  if (Math.abs(area) < 1e-10) {
    // Very small area, fall back to arithmetic mean
    const centerLat = coordinates.reduce((sum, [, lat]) => sum + lat, 0) / coordinates.length;
    const centerLng = coordinates.reduce((sum, [lng]) => sum + lng, 0) / coordinates.length;
    return { latitude: centerLat, longitude: centerLng };
  }

  // Final centroid calculation
  const factor = 1 / (6 * area);
  centroidLat *= factor;
  centroidLng *= factor;

  return { latitude: centroidLat, longitude: centroidLng };
}

/**
 * Calculate the bounding box of a polygon
 * @param coordinates Array of [longitude, latitude] coordinates
 * @returns The bounding box as {minLat, maxLat, minLng, maxLng}
 */
export function calculateBoundingBox(coordinates: [number, number][]): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} {
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;

  coordinates.forEach(([lng, lat]) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });

  return { minLat, maxLat, minLng, maxLng };
}

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param point The point to test {latitude, longitude}
 * @param polygonCoordinates Array of [longitude, latitude] coordinates forming the polygon
 * @returns true if the point is inside the polygon
 */
export function isPointInPolygon(point: LatLng, polygonCoordinates: [number, number][]): boolean {
  const { latitude, longitude } = point;
  let inside = false;

  for (let i = 0, j = polygonCoordinates.length - 1; i < polygonCoordinates.length; j = i++) {
    const coordI = polygonCoordinates[i];
    const coordJ = polygonCoordinates[j];
    if (!coordI || !coordJ) continue;
    const [lngI, latI] = coordI;
    const [lngJ, latJ] = coordJ;

    if (((latI > latitude) !== (latJ > latitude)) &&
        (longitude < (lngJ - lngI) * (latitude - latI) / (latJ - latI) + lngI)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Calculate the center of a polygon that's guaranteed to be inside the polygon
 * Falls back to centroid if the centroid is inside, otherwise finds the center of the largest inscribed circle
 * @param coordinates Array of [longitude, latitude] coordinates
 * @returns The center point as {latitude, longitude}
 */
export function calculatePolygonCenter(coordinates: [number, number][]): LatLng {
  // First try the true centroid
  const centroid = calculatePolygonCentroid(coordinates);
  
  // Check if centroid is inside the polygon
  if (isPointInPolygon(centroid, coordinates)) {
    return centroid;
  }

  // If centroid is outside, find the center of the bounding box
  // and check if it's inside the polygon
  const bbox = calculateBoundingBox(coordinates);
  const bboxCenter = {
    latitude: (bbox.minLat + bbox.maxLat) / 2,
    longitude: (bbox.minLng + bbox.maxLng) / 2
  };

  if (isPointInPolygon(bboxCenter, coordinates)) {
    return bboxCenter;
  }

  // If both centroid and bbox center are outside, 
  // find a point that's definitely inside by using the first vertex
  // and moving towards the centroid
  const firstCoord = coordinates[0];
  if (!firstCoord) {
    // Fallback to centroid if no coordinates available
    return centroid;
  }
  const [firstLng, firstLat] = firstCoord;
  const firstPoint = { latitude: firstLat, longitude: firstLng };
  
  // Interpolate between first point and centroid until we find a point inside
  for (let t = 0.1; t <= 0.9; t += 0.1) {
    const interpolatedPoint = {
      latitude: firstLat + (centroid.latitude - firstLat) * t,
      longitude: firstLng + (centroid.longitude - firstLng) * t
    };
    
    if (isPointInPolygon(interpolatedPoint, coordinates)) {
      return interpolatedPoint;
    }
  }

  // Last resort: return the first vertex
  return firstPoint;
}
